import { FC } from 'react';

import { Button } from '@purpur/library';
import { Icon } from '@purpur/library/icon';
import { close } from '@purpur/library/icon/assets/close';
import { remove } from '@purpur/library/icon/assets/remove';
import Image from 'next/image';

import { ButtonLink } from '@telialt/common-button-link/src';
import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { useProductMarketingTag } from '@telialt/core-search/src/hooks/useProductMarketingTag';
import { ProductSearchItem } from '@telialt/core-search/src/model';
import { getFullProductUrl } from '@telialt/core-search/src/utils/paths';

import { ESHOP_COMPARISON_TEST_ID } from '../../../constants/test-ids';
import {
    CardHeader,
    CompareActionsContainerDesktop,
    CompareActionsContainerMobile,
    CompareProductContainer,
    ImageContainer,
    MarketingTag,
    ProductLink,
    ProductName,
    StockStatus,
} from './CompareProductCard.styles';
import { ProductPricings } from './ProductPricings';

type Props = {
    variant?: 'full' | 'minimal';
    product: ProductSearchItem;
    onRemoveProduct: () => void;
};

export const CompareProductCard: FC<Props> = ({ variant = 'full', product, onRemoveProduct }) => {
    const { t } = useTranslations();

    const productUrl = getFullProductUrl(product.url, product.variantCode);
    const monthlyPrice = product.monthlyPriceFormatted;
    const fullPrice = product.fullPriceFormatted;
    const marketingTag = useProductMarketingTag(product);

    const stockStatusLabel =
        product.stockStatus === 'Turime prekyboje' ? undefined : t['search.component.product_card.out_of_stock'];

    const monthlyPriceLabel = t['search.component.product_card.monthly_price_label'];
    const monthlyPriceUnit = t['search.component.product_card.monthly_price_unit_accessible'];
    const fullPriceUnit = t['search.component.product_card.full_price_unit'];

    const monthlyPriceText = monthlyPrice ? `${monthlyPriceLabel} ${monthlyPrice} ${monthlyPriceUnit}` : '';
    const fullPriceText = fullPrice ? `${fullPrice} ${fullPriceUnit}` : '';
    const labelPrice = monthlyPriceText || fullPriceText;

    const ariaLabel = [product.name, labelPrice, stockStatusLabel].filter(Boolean).join(', ');

    return (
        <CompareProductContainer>
            <ProductLink href={productUrl} aria-label={ariaLabel} $variant={variant}>
                <CardHeader>
                    <ImageContainer>
                        <Image
                            src={product.img600x}
                            alt={product.name}
                            fill={true}
                            priority={true}
                            unoptimized={true}
                        />
                        {marketingTag && variant === 'full' && (
                            // TODO: marketing tag will be updated to dynamic with feature LTCONART-815, for now making this image decorative only
                            <MarketingTag src={marketingTag} alt={product.marketingTagAltText || ''} />
                        )}
                    </ImageContainer>
                    {variant === 'full' && <StockStatus>{stockStatusLabel}</StockStatus>}
                    <ProductName>{product.name}</ProductName>
                </CardHeader>
                {variant === 'full' && <ProductPricings product={product} />}
            </ProductLink>
            <CompareActionsContainerDesktop>
                <Button
                    variant="text"
                    size={variant === 'minimal' ? 'sm' : 'md'}
                    data-test={ESHOP_COMPARISON_TEST_ID.COMPARISON_REMOVE_PRODUCT}
                    onClick={onRemoveProduct}
                >
                    <Icon svg={close} size="xs" /> {t['eshop.comparison.remove_product']}
                </Button>
                <ButtonLink
                    variant="primary"
                    size={variant === 'minimal' ? 'sm' : 'md'}
                    href={productUrl}
                    dataTest={ESHOP_COMPARISON_TEST_ID.COMPARISON_SELECT_PRODUCT}
                >
                    {t['eshop.comparison.select_product']}
                </ButtonLink>
            </CompareActionsContainerDesktop>
            <CompareActionsContainerMobile>
                <Button
                    variant="text"
                    size="sm"
                    onClick={() => (window.location.href = productUrl)}
                    data-test={ESHOP_COMPARISON_TEST_ID.COMPARISON_SELECT_PRODUCT}
                >
                    {t['eshop.comparison.select_product']}
                </Button>
                <Button
                    variant="text"
                    size="sm"
                    data-test={ESHOP_COMPARISON_TEST_ID.COMPARISON_REMOVE_PRODUCT}
                    onClick={onRemoveProduct}
                    aria-label={t['eshop.comparison.remove_product']}
                >
                    <Icon svg={remove} size="xs" />
                </Button>
            </CompareActionsContainerMobile>
        </CompareProductContainer>
    );
};
